import {
  Typography,
  Button,
  alpha,
  useTheme,
  Stack,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import MenuIcon from "@mui/icons-material/Menu";
import { MenuItem } from "./types";
import VideoPlayer from "./VideoPlayer";
import { useTranslation } from "react-i18next";

interface RightPanelProps {
  currentItem: MenuItem;
  activeMenuItem: number;
  menuItemsLength: number;
  onNext: () => void;
  onBack: () => void;
  onFinish: () => void;
  onClose?: () => void;
  onMenuToggle?: () => void;
  isSmallScreen?: boolean;
}

export default function RightPanel({
  currentItem,
  activeMenuItem,
  menuItemsLength,
  onNext,
  onBack,
  onFinish,
  onClose,
  onMenuToggle,
  isSmallScreen,
}: RightPanelProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const isDesktop = useMediaQuery("(min-width: 1200px)");

  const headerHeight = 48;
  const navigationHeight = 76;

  return (
    <>
      {/* Tab Bar Header Section - Sticky for mobile/tablet */}
      {onClose && (
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            p: 2,
            minHeight: `${headerHeight}px`,
            backgroundColor: "background.paper",
            // Make sticky on tablets and mobile
            [theme.breakpoints.down("lg")]: {
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              zIndex: theme.zIndex.appBar,
            },
            [theme.breakpoints.up("lg")]: {
              position: "relative",
            },
          }}
        >
          {/* Left: Hamburger Menu (small screens only) */}
          {isSmallScreen && onMenuToggle && (
            <IconButton
              onClick={onMenuToggle}
              size="small"
              sx={{
                p: 0.5,
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              <MenuIcon sx={{ fontSize: "20px" }} />
            </IconButton>
          )}

          {/* Center spacer */}
          <div style={{ flexGrow: 1 }} />

          {/* Right: Close Button */}
          <IconButton
            onClick={onClose}
            id="close-button-right-panel"
            size="small"
            sx={{
              p: 0.5,
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              },
            }}
          >
            <CloseOutlinedIcon sx={{ fontSize: "20px" }} />
          </IconButton>
        </Stack>
      )}

      {/* Main Scrollable Content Area */}
      <Stack
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          position: "relative",
          [theme.breakpoints.down("lg")]: {
            height: `calc(100vh - ${headerHeight + navigationHeight}px)`,
            overflowY: "auto",
            marginTop: onClose ? `${headerHeight}px` : 0,
            marginBottom: `${navigationHeight}px`,
            WebkitOverflowScrolling: "touch",
          },
          [theme.breakpoints.up("lg")]: {
            height: `calc(100vh - ${navigationHeight}px)`,
            overflowY: "auto",
          },
        }}
      >
        {/* Central Content Container */}
        {currentItem.videoUrl ? (
          isDesktop ? (
            <Stack
              sx={{
                flex: 1,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 0,
              }}
            >
              <VideoPlayer
                src={currentItem.videoUrl}
                coverImage={currentItem.coverImage}
                maxHeight="60vh"
                maxWidth="100%"
              />
            </Stack>
          ) : (
            <VideoPlayer
              src={currentItem.videoUrl}
              coverImage={currentItem.coverImage}
            />
          )
        ) : (
          <Stack
            sx={{
              flex: 1,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Typography
              variant="h2"
              sx={{
                whiteSpace: "nowrap",
                textAlign: "center",
              }}
            >
              {currentItem.title}
            </Typography>
          </Stack>
        )}

        {/* Bottom Content Section - Dynamic */}
        <Stack
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            p: 5,
            gap: "8px",
            backgroundColor: alpha(theme.palette.background.paper, 0.8),
            flexShrink: 0,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              mb: 1,
            }}
          >
            {currentItem.subtitle}
          </Typography>

          <Typography
            variant="body1"
            sx={{
              maxWidth: "100%",
            }}
          >
            {currentItem.description}
          </Typography>
        </Stack>
      </Stack>

      {/* Navigation Buttons */}
      <Stack
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          p: "24px 40px",
          gap: "8px",
          backgroundColor: "background.paper",
          height: `${navigationHeight}px`,
          flexShrink: 0,
          [theme.breakpoints.down("lg")]: {
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: theme.zIndex.appBar,
            p: "16px 20px",
          },
          [theme.breakpoints.up("lg")]: {
            position: "relative",
          },
        }}
      >
        <Stack
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "flex-start",
            gap: "16px",
          }}
        >
          {activeMenuItem !== 0 && (
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={onBack}
              sx={{
                minWidth: "112px",
                height: "40px",
                border: "1px solid",
                borderColor: "primary.main",
                borderOpacity: 0.5,
                color: "primary.dark",
              }}
            >
              {t("buttons.back")}
            </Button>
          )}
        </Stack>
        <Stack
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "flex-start",
            gap: "16px",
          }}
        >
          <Button
            variant="contained"
            endIcon={
              activeMenuItem === menuItemsLength - 1 ? null : (
                <ArrowForwardIcon />
              )
            }
            onClick={activeMenuItem === menuItemsLength - 1 ? onFinish : onNext}
            sx={{
              minWidth: "111px",
              height: "40px",
              backgroundColor: "primary.main",
              color: "primary.contrastText",
              "&:hover": {
                backgroundColor: "primary.dark",
              },
            }}
          >
            {activeMenuItem === menuItemsLength - 1
              ? t("buttons.finish")
              : t("buttons.next")}
          </Button>
        </Stack>
      </Stack>
    </>
  );
}
